import { EnvironmentOutlined } from "@ant-design/icons"
import { Checkbox, Col, Flex, Row, Select, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { AddressAutoComplete } from "../../../../components/AutoComplete/AddressAutoComplete.tsx"
import { ZipLookupHint } from "../../../../components/AutoComplete/ZipLookupHint.tsx"
import { AddressMapPreview } from "../../../../components/Shared/AddressMapPreview.tsx"
import { AddressQuickLinks } from "../../../../components/Shared/AddressQuickLinks.tsx"
import { useZipLookup } from "../../../../hooks/use-zip-lookup.ts"
import { zipCodeRegexUS } from "../../../../utils/validation/zip-code.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { type LeadFormDataCustomer } from "../../LeadAddPage/components/lead-form-data.ts"
import { applyAddressSuggestion } from "../../helpers/address-suggestion.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import {
  type LeadPatchSender,
  type LeadWorkspacePatch,
  pickChangedFields
} from "../lead-workspace-patch.ts"
import { LeadOverviewField } from "./LeadOverviewField.tsx"
import {
  leadCustomerAddressLine,
  leadCustomerValues
} from "./lead-overview-customer.ts"
import { OverviewCardTitle } from "./OverviewCardIcon.tsx"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

interface AddressValues {
  address?: string | null
  address_2?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}

interface LocationCardValues extends AddressValues {
  customer: LeadFormDataCustomer
  use_customer_address: boolean
}

const propertyAddressLine = (lead: AddressValues | null) =>
  [lead?.address, lead?.address_2, lead?.city, lead?.state, lead?.zip_code]
    .filter(Boolean)
    .join(", ") || null

/** The same rule the form's map preview applies: city and state, valid ZIP. */
function mapAddress(values: {
  address?: string | null
  city?: string | null
  state?: string | null
  zip_code?: string | null
}): string | null {
  const city = values.city?.trim()
  const state = values.state?.trim()
  if (!city || !state) return null

  const zip = values.zip_code?.trim().toUpperCase()
  const parts = [
    values.address?.trim(),
    city,
    state,
    zip && zipCodeRegexUS.test(zip) ? zip : null
  ].filter(Boolean)

  return `${parts.join(", ")}, USA`
}

interface LocationAddressEditorProps {
  title: string
  values: AddressValues
  disabled: boolean
  knownStates: string[]
  stateOptions: LeadsFilterOptions["state"]
  onChange: (patch: Partial<AddressValues>) => void
  ariaPrefix?: string
  autoFocus?: boolean
}

function LocationAddressEditor({
  title,
  values,
  disabled,
  knownStates,
  stateOptions,
  onChange,
  ariaPrefix,
  autoFocus
}: LocationAddressEditorProps) {
  const { t } = useTranslation()
  const ariaLabel = (label: string) =>
    ariaPrefix ? `${ariaPrefix} ${label}` : label

  const { lookupZip, chooseMatch, suppressLookupFor, status } = useZipLookup({
    knownStates,
    disabled,
    initialZip: values.zip_code,
    onFill: onChange
  })

  return (
    <>
      <Col xs={24}>
        <Typography.Text strong>{title}</Typography.Text>
      </Col>
      <Col xs={24}>
        <LeadOverviewField
          label={t("leadWorkspace.cards.location.addressSearch")}
        >
          <AddressAutoComplete
            disabled={disabled}
            placeholder={
              ariaPrefix ? `Search ${title.toLowerCase()}...` : undefined
            }
            onSelect={(suggestion) => {
              const applied = applyAddressSuggestion(suggestion, knownStates)
              suppressLookupFor(applied.zip_code)
              onChange(applied)
            }}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.address")}>
          <TextInput
            autoFocus={autoFocus}
            disabled={disabled}
            aria-label={ariaLabel(
              t("leadWorkspace.cards.location.address")
            )}
            value={values.address ?? ""}
            onChange={(event) =>
              onChange({ address: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={12}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.address2")}>
          <TextInput
            disabled={disabled}
            aria-label={ariaLabel(
              t("leadWorkspace.cards.location.address2")
            )}
            value={values.address_2 ?? ""}
            onChange={(event) =>
              onChange({ address_2: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.city")}>
          <TextInput
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.city"))}
            value={values.city ?? ""}
            onChange={(event) =>
              onChange({ city: event.target.value || null })
            }
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.state")}>
          <Select
            allowClear
            showSearch
            className={"w-full"}
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.state"))}
            optionFilterProp={"label"}
            options={stateOptions ?? []}
            value={values.state ?? undefined}
            onChange={(next) => onChange({ state: next ?? null })}
          />
        </LeadOverviewField>
      </Col>
      <Col xs={24} sm={8}>
        <LeadOverviewField label={t("leadWorkspace.cards.location.zip")}>
          <TextInput
            disableAutoCapitalize
            disabled={disabled}
            aria-label={ariaLabel(t("leadWorkspace.cards.location.zip"))}
            value={values.zip_code ?? ""}
            onChange={(event) => {
              onChange({ zip_code: event.target.value || null })
              lookupZip(event.target.value)
            }}
          />
          <ZipLookupHint status={status} onChoose={chooseMatch} />
        </LeadOverviewField>
      </Col>
    </>
  )
}

interface LocationEditBodyProps {
  values: LocationCardValues
  setValues: (patch: Partial<LocationCardValues>) => void
  saving: boolean
  linkedCustomerId: string | null
  knownStates: string[]
  stateOptions: LeadsFilterOptions["state"]
  applyCustomerAddress: (
    address: LeadFormDataCustomer
  ) => Partial<LocationCardValues>
}

type AddressLink = "none" | "customer" | "property"

/**
 * Rendered only while the card is in edit mode, so its `link` state resets to
 * "none" every time editing reopens: the persisted `use_customer_address` never
 * locks an editor on open, and re-linking either direction is an explicit tick.
 */
function LocationEditBody({
  values,
  setValues,
  saving,
  linkedCustomerId,
  knownStates,
  stateOptions,
  applyCustomerAddress
}: LocationEditBodyProps) {
  const { t } = useTranslation()
  const [link, setLink] = useState<AddressLink>("none")

  const applyPropertyAddress = (
    current: LocationCardValues
  ): Partial<LocationCardValues> => ({
    customer: {
      ...current.customer,
      address: current.address ?? null,
      address_2: current.address_2 ?? null,
      city: current.city ?? null,
      state: current.state ?? null,
      zip_code: current.zip_code ?? null
    },
    use_customer_address: true
  })

  // A live link needs its source Address 1 filled; clearing it would unmount
  // the checkbox and strand the mirrored editor locked with no way to untick.
  useEffect(() => {
    if (
      (link === "customer" && !values.customer.address) ||
      (link === "property" && !values.address)
    ) {
      setLink("none")
      setValues({ use_customer_address: false })
    }
  }, [values.address, values.customer.address, link, setValues])

  const forwardLinked = link === "customer"
  const reverseLinked = link === "property"

  return (
    <Row gutter={[12, 8]}>
      {!!linkedCustomerId && !!values.address && link !== "customer" && (
        <Col xs={24}>
          <Checkbox
            disabled={saving}
            checked={reverseLinked}
            onChange={(event) => {
              if (event.target.checked) {
                setLink("property")
                setValues(applyPropertyAddress(values))
              } else {
                setLink("none")
                setValues({ use_customer_address: false })
              }
            }}
          >
            {t("leadWorkspace.cards.location.applyPropertyAddress")}
          </Checkbox>
        </Col>
      )}
      <LocationAddressEditor
        // Remount on a reverse link/unlink so the ZIP hook re-reads the ZIP a
        // copied property address left in the fields, mirroring the property
        // editor's own key below. Namespaced so it never collides with that
        // sibling's `String(forwardLinked)`.
        key={`customer-${reverseLinked}`}
        title={t("leadWorkspace.cards.location.customerAddress")}
        values={values.customer}
        disabled={saving || reverseLinked}
        knownStates={knownStates}
        stateOptions={stateOptions}
        ariaPrefix={t("leadWorkspace.cards.customer.title")}
        onChange={(customerPatch) => {
          const nextCustomer = {
            ...values.customer,
            ...customerPatch
          }
          setValues({
            customer: nextCustomer,
            ...(forwardLinked
              ? applyCustomerAddress(nextCustomer)
              : { use_customer_address: false })
          })
        }}
      />
      {!!linkedCustomerId && !!values.customer.address && link !== "property" && (
        <Col xs={24}>
          <Checkbox
            disabled={saving}
            checked={forwardLinked}
            onChange={(event) => {
              if (event.target.checked) {
                setLink("customer")
                setValues(applyCustomerAddress(values.customer))
              } else {
                setLink("none")
                setValues({ use_customer_address: false })
              }
            }}
          >
            {t("leadWorkspace.cards.location.sameAsCustomer")}
          </Checkbox>
        </Col>
      )}
      <LocationAddressEditor
        // Remount so the hook re-reads the ZIP the copied address left in the fields.
        key={String(forwardLinked)}
        title={t("leadWorkspace.cards.location.propertyAddress")}
        values={values}
        disabled={saving || forwardLinked}
        knownStates={knownStates}
        stateOptions={stateOptions}
        autoFocus
        onChange={(propertyPatch) => {
          const nextProperty = { ...values, ...propertyPatch }
          setValues({
            ...propertyPatch,
            ...(reverseLinked
              ? applyPropertyAddress(nextProperty)
              : { use_customer_address: false })
          })
        }}
      />
    </Row>
  )
}

interface LeadOverviewLocationCardProps {
  lead: Lead
  filterOptions: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadOverviewLocationCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadOverviewLocationCardProps) {
  const { t } = useTranslation()

  const knownStates = useMemo(
    () => (filterOptions?.state ?? []).map((option) => String(option?.value)),
    [filterOptions?.state]
  )

  const customer = leadCustomerValues(lead)
  const customerAddress = leadCustomerAddressLine(customer)
  const linkedCustomerId = lead?.customer?.id ?? null

  const values: LocationCardValues = {
    customer,
    address: lead?.address ?? null,
    address_2: lead?.address_2 ?? null,
    city: lead?.city ?? null,
    state: lead?.state ?? null,
    zip_code: lead?.zip_code ?? null,
    use_customer_address: !!lead?.use_customer_address
  }

  const applyCustomerAddress = (
    address: LeadFormDataCustomer
  ): Partial<LocationCardValues> => ({
    address: address.address ?? null,
    address_2: address.address_2 ?? null,
    city: address.city ?? null,
    state: address.state ?? null,
    zip_code: address.zip_code ?? null,
    use_customer_address: true
  })

  const saveLocation = (changed: Partial<LocationCardValues>) => {
    const patch: LeadWorkspacePatch = { ...changed }

    if (changed.customer) {
      const editedCustomer = pickChangedFields(customer, changed.customer)
      if (Object.keys(editedCustomer).length) {
        patch.customer = editedCustomer
        if (linkedCustomerId) patch.customer_id = linkedCustomerId
      } else delete patch.customer
    }

    return onPatch(patch)
  }

  return (
    <WorkspaceCard<LocationCardValues>
      cardId={"location"}
      title={
        <OverviewCardTitle icon={<EnvironmentOutlined />} accent={"indigo"}>
          {t("leadWorkspace.cards.location.title")}
        </OverviewCardTitle>
      }
      ariaTitle={t("leadWorkspace.cards.location.title")}
      className={"h-full"}
      canEdit={canEdit}
      values={values}
      onSave={saveLocation}
      renderEdit={({ values, setValues, saving }) => (
        <LocationEditBody
          values={values}
          setValues={setValues}
          saving={saving}
          linkedCustomerId={linkedCustomerId}
          knownStates={knownStates}
          stateOptions={filterOptions?.state}
          applyCustomerAddress={applyCustomerAddress}
        />
      )}
    >
      <Row gutter={[16, 12]} align={"stretch"} className={"flex-1"}>
        <Col xs={24} md={16}>
          <Flex vertical justify={"center"} gap={"middle"} className={"h-full"}>
            <Row gutter={[16, 8]}>
              <Col
                span={12}
                className={"border-r border-border"}
              >
                <LeadOverviewField
                  label={t("leadWorkspace.cards.location.customerAddress")}
                  value={customerAddress}
                />
              </Col>
              <Col span={12}>
                <LeadOverviewField
                  label={t("leadWorkspace.cards.location.propertyAddress")}
                  value={propertyAddressLine(lead)}
                />
                <AddressQuickLinks
                  className="mt-1"
                  address={lead.address}
                  address_2={lead.address_2}
                  city={lead.city}
                  state={lead.state}
                  zip_code={lead.zip_code}
                  geolocation={lead.geolocation}
                />
              </Col>
            </Row>
          </Flex>
        </Col>
        <Col xs={24} md={8} className={"min-h-[160px]"}>
          <AddressMapPreview
            height={"100%"}
            className={"mb-0 h-full"}
            fullAddress={mapAddress(lead)}
            emptyText={t("leadWorkspace.cards.location.noMap")}
          />
        </Col>
      </Row>
    </WorkspaceCard>
  )
}

export {
  type LocationCardValues,
  mapAddress,
  propertyAddressLine,
  LeadOverviewLocationCard
}
